import ESign from '@/lib/e-imzo/e-sign';
import { normalizeError } from '@/errors/normalize';

/**
 * EDS (Electronic Digital Signature) service for E-IMZO integration
 */
class EDSService {
  constructor() {
    this.client = new ESign();
  }

  /**
   * Install E-IMZO client
   */
  async install() {
    try {
      const response = await this.client.install();
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      throw {
        success: false,
        error: normalizeError(error),
        type: 'installation_error',
      };
    }
  }

  /**
   * List all available user keys/certificates
   */
  async listAllUserKeys() {
    try {
      await this.install();

      // Add delay to allow WebSocket connection to stabilize
      await new Promise(resolve => setTimeout(resolve, 1000));

      const keys = await this.client.listAllUserKeys();
      return {
        success: true,
        data: keys,
      };
    } catch (error) {
      const normalizedError = normalizeError(error);

      // Check for WebSocket connection errors
      if (error.message && error.message.includes('WebSocket')) {
        throw {
          success: false,
          error: {
            ...normalizedError,
            message:
              'E-IMZO connection failed. Please ensure E-IMZO is installed and running.',
            userMessage:
              'E-IMZO service is not available. Please check your installation.',
          },
          type: 'connection_error',
        };
      }

      throw {
        success: false,
        error: normalizedError,
        type: 'key_listing_error',
      };
    }
  }

  /**
   * Load specific key for signing
   */
  async loadKey(certificate) {
    try {
      const keyData = await this.client.loadKey(certificate);
      return {
        success: true,
        data: keyData,
      };
    } catch (error) {
      throw {
        success: false,
        error: normalizeError(error),
        type: 'key_loading_error',
      };
    }
  }

  /**
   * Create PKCS7 signature
   */
  async createPkcs7(keyId, guid) {
    try {
      const pkcs = await this.client.createPkcs7(keyId, guid);
      return {
        success: true,
        data: pkcs,
      };
    } catch (error) {
      throw {
        success: false,
        error: normalizeError(error),
        type: 'signing_error',
      };
    }
  }

  /**
   * Complete EDS authentication flow
   */
  async authenticateWithEDS(certificate, guid) {
    try {
      // Load the key
      const keyResult = await this.loadKey(certificate);
      if (!keyResult.success) {
        throw keyResult.error;
      }

      // Create signature
      const pkcsResult = await this.createPkcs7(keyResult.data.id, guid);
      if (!pkcsResult.success) {
        throw pkcsResult.error;
      }

      return {
        success: true,
        data: {
          keyId: keyResult.data,
          pkcs: pkcsResult.data,
        },
      };
    } catch (error) {
      throw {
        success: false,
        error: normalizeError(error),
        type: 'authentication_error',
      };
    }
  }
}

// Export singleton instance
export const edsService = new EDSService();
